import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Button, message, Table, InputNumber, Switch, Tag, Progress, Upload, Icon } from "antd";
import { editProduct } from '../../../../../../redux/actions/productActions';
import strings from '../../../../../../strings';
import { connect } from 'react-redux';
import { transformInToFormObject } from '../../../../../../utils/generateFormData';

const BulkQuantityEdit = ({
    open,
    onCancel,
    selectedRowKeys,
    products,
    updateProducts,
    editProduct,
    cancelSelection
}) => {
    const [ loading, setLoading ] = useState(false);
    const [ productToEdit, setProductToEdit ] = useState([]);
    const [ progress, setProgress ] = useState({ show: false, percent: 0, current: 0, total: 0 });

    const BATCH_SIZE = 5;
    const BATCH_DELAY_MS = 1500;
    const MAX_IMAGE_SIZE_MB = 5;

    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    useEffect(() => {
        const productsObj = {};
        for (let i of products) {
            productsObj[i.id] = i;
        }
        
        const productsToSend = selectedRowKeys 
        && selectedRowKeys
        .map((id) => {
            const prod = productsObj[Number(id)];
            if (!prod) return null;
            // Deep clone so we don't mutate original product data
            const clone = { ...prod };
            if (clone.variants && clone.variants.length > 0) {
                clone.variants = clone.variants.map((v, idx) => ({
                    ...v,
                    _variantKey: `${clone.id}-variant-${idx}`,
                    quantityToAdd: null
                }));
            }
            return clone;
        })
        .filter((prod) => prod);

        setProductToEdit(productsToSend);
    }, [products, selectedRowKeys])

    const hasVariants = (product) => product.variants && product.variants.length > 0;

    // Columns for the main product table
    const columns = [
        {
          title: "Product Name",
          dataIndex: "name",
          render: (val, record) => (
            <span>
              {val}
              {hasVariants(record) && (
                <Tag color="blue" style={{ marginLeft: 8 }}>
                  {record.variants.length} variant{record.variants.length > 1 ? 's' : ''}
                </Tag>
              )}
            </span>
          ),
        },
        {
          title: "Current Quantity",
          dataIndex: "quantity",
          render: (val, record) => {
            const current = val || 0;
            if (hasVariants(record) && record._variantQtyPreview != null) {
              return (
                <span>
                  {current} <span style={{ color: '#1890ff' }}>→ {record._variantQtyPreview}</span>
                </span>
              );
            }
            if (!hasVariants(record) && record.quantityToAdd) {
              const newQty = Number(current) + Number(record.quantityToAdd);
              return (
                <span>
                  {current} <span style={{ color: '#1890ff' }}>→ {newQty}</span>
                </span>
              );
            }
            return current;
          },
        },
        {
          title: "Amount to add",
          dataIndex: "quantity",
          render: (val, record) => {
            // For products with variants, quantity is managed per-variant
            if (hasVariants(record)) {
              return <span style={{ color: '#999', fontStyle: 'italic' }}>See variants below</span>;
            }
            return (
              <InputNumber
                min={1}
                onChange={(val) => updateProductQuantity(record.id, val)}
              />
            );
          },
        },
        {
            title: "Track Product Inventory",
            dataIndex: "track_inventory",
            render: (val, record) => (
              <Switch
                checked={val}
                onChange={(val) => updateProductTrackINV(record.id, val)}
              />
            ),
        },
    ];

    // Columns for the variant sub-table
    const variantColumns = [
        {
          title: "Variant",
          render: (_, record) => `${record.type}: ${record.value}`,
        },
        {
          title: "Current Quantity",
          dataIndex: "quantity",
          render: (val) => val || 0,
        },
        {
          title: "Amount to add",
          render: (_, record) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <InputNumber
                min={1}
                value={record.quantityToAdd}
                onChange={(val) => updateVariantQuantity(record._productId, record._variantIndex, val)}
              />
              <Button
                size="small"
                type="link"
                onClick={() => applyQuantityToAllVariants(record._productId, record.quantityToAdd)}
              >
                Apply to all
              </Button>
            </div>
          ),
        },
        {
          title: "Price",
          dataIndex: "price",
          render: (val) => val ? Number(val).toLocaleString() : '-',
        },
        {
          title: "Image",
          dataIndex: "picture",
          width: 120,
          render: (val, record) => {
            const preview = record._newPicturePreview || val;
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {preview && (
                  <img
                    src={preview}
                    alt="variant"
                    style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4 }}
                  />
                )}
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
                      message.error(`Image must be smaller than ${MAX_IMAGE_SIZE_MB}MB`);
                      return Upload.LIST_IGNORE;
                    }
                    updateVariantImage(record._productId, record._variantIndex, file);
                    return false;
                  }}
                >
                  <Button size="small" icon="upload" />
                </Upload>
                {record._newPicturePreview && (
                  <Button
                    size="small"
                    icon="close"
                    type="link"
                    style={{ color: '#f5222d' }}
                    onClick={() => clearVariantImage(record._productId, record._variantIndex)}
                  />
                )}
              </div>
            );
          },
        },
    ];

    const applyQuantityToAllVariants = (productId, quantity) => {
        if (!quantity) return message.error("Please enter a quantity first");

        setProductToEdit((prev) => {
            return prev.map(item => {
                if (item.id === productId) {
                    const updatedVariants = item.variants.map(v => ({
                        ...v,
                        quantityToAdd: Number(quantity)
                    }));
                    const totalQty = updatedVariants.reduce((sum, v) => {
                        const current = Number(v.quantity) || 0;
                        const toAdd = Number(v.quantityToAdd) || 0;
                        return sum + current + toAdd;
                    }, 0);
                    return { ...item, variants: updatedVariants, _variantQtyPreview: totalQty };
                }
                return item;
            })
        })
    }

    const updateProductQuantity = (id, quantity) => {
        if (!quantity) return message.error("Please enter quantity");

        setProductToEdit((prev) => {
            return prev.map(item => {
                if(item.id === id){
                    return { ...item, quantityToAdd: Number(quantity) };
                }
                return item;
            })
        })
    }

    const updateVariantQuantity = (productId, variantIndex, quantity) => {
        if (!quantity) return;

        setProductToEdit((prev) => {
            return prev.map(item => {
                if (item.id === productId) {
                    const updatedVariants = item.variants.map((v, idx) => {
                        if (idx === variantIndex) {
                            return { ...v, quantityToAdd: Number(quantity) };
                        }
                        return v;
                    });
                    // Recalculate total product quantity from variants
                    const totalQty = updatedVariants.reduce((sum, v) => {
                        const current = Number(v.quantity) || 0;
                        const toAdd = Number(v.quantityToAdd) || 0;
                        return sum + current + toAdd;
                    }, 0);
                    return { ...item, variants: updatedVariants, _variantQtyPreview: totalQty };
                }
                return item;
            })
        })
    }

    const updateVariantImage = (productId, variantIndex, file) => {
        const previewUrl = URL.createObjectURL(file);
        setProductToEdit((prev) => {
            return prev.map(item => {
                if (item.id === productId) {
                    const updatedVariants = item.variants.map((v, idx) => {
                        if (idx === variantIndex) {
                            return { ...v, _newPicture: file, _newPicturePreview: previewUrl };
                        }
                        return v;
                    });
                    return { ...item, variants: updatedVariants };
                }
                return item;
            });
        });
    };

    const clearVariantImage = (productId, variantIndex) => {
        setProductToEdit((prev) => {
            return prev.map(item => {
                if (item.id === productId) {
                    const updatedVariants = item.variants.map((v, idx) => {
                        if (idx === variantIndex) {
                            if (v._newPicturePreview) URL.revokeObjectURL(v._newPicturePreview);
                            return { ...v, _newPicture: null, _newPicturePreview: null };
                        }
                        return v;
                    });
                    return { ...item, variants: updatedVariants };
                }
                return item;
            });
        });
    };

    const updateProductTrackINV = (id, value) => {
        setProductToEdit((prev) => {
            return prev.map(item => {
                if(item.id === id){
                    return { ...item, track_inventory: value };
                }
                return item;
            })
        })
    }

    const handleBulkProductEdit = async () => {
        // Build final payloads — only include products with actual changes
        const productsPayload = productToEdit.map(product => {
            const clone = { ...product };
            let hasChanges = false;

            if (hasVariants(clone)) {
                // Check if any variant has a quantity to add or an image change
                const anyVariantChanged = clone.variants.some(v => v.quantityToAdd || v._newPicture);
                if (!anyVariantChanged) return null;

                hasChanges = true;
                // Update variant quantities
                clone.variants = clone.variants.map(v => {
                    const updated = { ...v };
                    if (updated.quantityToAdd) {
                        updated.quantity = (Number(updated.quantity) || 0) + Number(updated.quantityToAdd);
                    }
                    // Apply new picture if set
                    if (updated._newPicture) {
                        updated.picture = updated._newPicture;
                    }
                    // Clean up internal fields
                    delete updated.quantityToAdd;
                    delete updated._variantKey;
                    delete updated._productId;
                    delete updated._variantIndex;
                    delete updated._newPicture;
                    delete updated._newPicturePreview;
                    return updated;
                });
                // Recalculate total product quantity from variants
                clone.quantity = clone.variants.reduce(
                    (sum, v) => sum + (Number(v.quantity) || 0), 0
                );
            } else {
                // Simple product — skip if no quantity to add and no track_inventory change
                const original = products.find(p => p.id === clone.id);
                const trackChanged = original && original.track_inventory !== clone.track_inventory;

                if (!clone.quantityToAdd && !trackChanged) return null;

                hasChanges = true;
                if (clone.quantityToAdd && clone.quantity) {
                    clone.quantity = Number(clone.quantity) + Number(clone.quantityToAdd);
                }
                if (clone.quantityToAdd && !clone.quantity) {
                    clone.quantity = Number(clone.quantityToAdd);
                }
                delete clone.quantityToAdd;
            }

            return hasChanges ? clone : null;
        }).filter(Boolean);

        if (productsPayload.length === 0) {
            return Modal.info({ title: "No changes to save. Please enter quantity for at least one product or variant." });
        }

        const isNullProd = productsPayload.filter(product => {
            // Only validate products where the user actually toggled track_inventory on
            const original = products.find(p => p.id === product.id);
            const wasTrackingBefore = original && original.track_inventory;
            const justEnabledTracking = product.track_inventory && !wasTrackingBefore;
            return justEnabledTracking && (product.quantity === null || product.quantity === undefined || product.quantity === "");
        });
        if(isNullProd.length > 0) return Modal.info({
            title: "Product with inventory tracking must have quantity set"
        });

        setLoading(true);

        const requests = productsPayload.map((product) => {
            const payload = {
                cost_price: product.cost_price,
                price: product.price,
                product_id: product.product_id,
                track_inventory: product.track_inventory,
                product_sku: product.product_sku,
                publish_to_loystar_shop: product.publish_to_loystar_shop,
                tax: product.tax,
                name: product.name,
                has_custom_qty: product.has_custom_qty,
                unit: product.unit,
                quantity: product.quantity
            };

            if (product.merchant_product_category_id) {
                payload.merchant_product_category_id = product.merchant_product_category_id;
            }
            if (product.merchant_loyalty_program_id && product.merchant_loyalty_program_id !== 0) {
                payload.merchant_loyalty_program_id = product.merchant_loyalty_program_id;
            }

            
            if (hasVariants(product)) {
                payload.variants = product.variants.map(v => {
                    const obj = {};
                    for (let key in v) {
                        if (v[key] !== null && v[key] !== undefined) {
                            obj[key] = v[key];
                        }
                    }
                    return obj;
                });
            }

            return { id: product.id, formData: transformInToFormObject(payload) };
        });

        const total = requests.length;
        setProgress({ show: true, percent: 0, current: 0, total });

        try {
            let completed = 0;

            for (let i = 0; i < total; i += BATCH_SIZE) {
                const batch = requests.slice(i, i + BATCH_SIZE);
                await Promise.all(batch.map(req => editProduct(req.id, req.formData)));
                completed += batch.length;
                const percent = Math.round((completed / total) * 100);
                setProgress({ show: true, percent, current: completed, total });
                // Yield to browser so the progress bar actually paints
                await new Promise(r => setTimeout(r, 50));
            }

            setLoading(false);
            setProgress({ show: false, percent: 100, current: total, total });
            Modal.success({
                title: strings.productsEditedSuccessfully,
                onOk: () => {
                    updateProducts();
                    cancelSelection();
                    onCancel();
                }
            });
        } catch (err) {
            setLoading(false);
            setProgress({ show: false, percent: 0, current: 0, total: 0 });
            Modal.error({
                title: "An error occurred while updating products",
                onOk: () => {
                    onCancel();
                    updateProducts();
                }
            });
        }
    }

    // Expandable row config for products with variants
    const expandable = {
        expandedRowRender: (record) => {
            if (!hasVariants(record)) return null;
            const variantData = record.variants.map((v, idx) => ({
                ...v,
                _productId: record.id,
                _variantIndex: idx,
                key: v._variantKey || `${record.id}-v-${idx}`,
            }));
            return (
                <Table
                    columns={variantColumns}
                    dataSource={variantData}
                    pagination={false}
                    size="small"
                    style={{ margin: '0' }}
                />
            );
        },
        rowExpandable: (record) => hasVariants(record),
    };

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            okText="Edit Products"
            title="Bulk Quantity Update"
            okButtonProps={{loading: loading}}
            onOk={handleBulkProductEdit}
            width={800}
            closable={!loading}
            maskClosable={!loading}
        >
            <p style={{ marginBottom: 12, color: '#666' }}>
                Products with variants can be expanded to update quantity per variant.
            </p>
            <Table
                columns={columns}
                dataSource={productToEdit}
                expandable={expandable}
                rowKey="id"
            />
            {progress.show && (
                <div style={{ marginTop: 12, padding: '8px 0' }}>
                    <Progress 
                        percent={progress.percent} 
                        status="active" 
                        size="small"
                        format={() => `${progress.current}/${progress.total}`}
                    />
                </div>
            )}
        </Modal>
    )
}

const mapStateToProps = (state) => ({
    state: state
});
export default connect(mapStateToProps, {editProduct})(BulkQuantityEdit);